/**
 * Where DYMO Connect's local web service listens. Shared because two sides need
 * the same list: the web client probes these addresses to find the service
 * (packages/web/src/labels/dymo-service.ts), and the API's Content-Security-Policy
 * must name every one of them in `connect-src` or the browser refuses the probe
 * (packages/api/src/app.ts).
 *
 * DYMO Connect for Desktop installs a small HTTPS service on the computer it runs
 * on. It binds the first free port in 41951-41960 on the loopback address, under
 * a certificate DYMO's installer adds to the operating system's trust store. The
 * values are the ones DYMO's own JavaScript framework uses
 * (github.com/dymosoftware/dymo-connect-framework: WS_PROTOCOL, WS_SVC_HOST,
 * WS_SVC_HOST_LEGACY, WS_START_PORT, WS_END_PORT, WS_SVC_PATH).
 */

/** The loopback names the service answers on: the current one, then the legacy one. */
export const DYMO_SERVICE_HOSTS = ['127.0.0.1', 'localhost'] as const;

export const DYMO_SERVICE_FIRST_PORT = 41951;
export const DYMO_SERVICE_LAST_PORT = 41960;

/** The path every command of the service lives under. */
export const DYMO_SERVICE_PATH = '/DYMO/DLS/Printing';

/** The ports the service may be on, lowest first. */
export function dymoServicePorts(): number[] {
  const ports: number[] = [];
  for (let port = DYMO_SERVICE_FIRST_PORT; port <= DYMO_SERVICE_LAST_PORT; port += 1) {
    ports.push(port);
  }
  return ports;
}

/** The origins of one host, lowest port first: `https://127.0.0.1:41951`, ... */
export function dymoServiceOriginsFor(host: (typeof DYMO_SERVICE_HOSTS)[number]): string[] {
  return dymoServicePorts().map((port) => `https://${host}:${port}`);
}

/**
 * Every origin the service may answer on (both hosts, all ten ports). A
 * Content-Security-Policy source cannot express a port range, so the policy
 * lists them one by one rather than opening every loopback port with `:*`.
 */
export function dymoServiceOrigins(): string[] {
  return DYMO_SERVICE_HOSTS.flatMap((host) => dymoServiceOriginsFor(host));
}

/** Whether a string is exactly one of the service's possible origins. */
export function isDymoServiceOrigin(value: string): boolean {
  return dymoServiceOrigins().includes(value);
}
